import {lineAnchorFrom, lineOrientationFrom} from '@github-ui/diffs/diff-line-helpers'
import type {DiffAnchor} from '@github-ui/diffs/types'
import {Fragment, memo, type MouseEvent, type RefObject, useEffect, useMemo} from 'react'

import {DiffLineContextProvider} from '../contexts/DiffLineContext'
import type {
  UpdateSelectedDiffRowRangeFnArgs,
  ReplaceSelectedDiffRowRangeFromGridCellsFnArgs,
} from '../contexts/SelectedDiffRowRangeContext'
import DiffRowSelectedChecker from '../helpers/diff-row-selected-checker'
import {getDiffHunksData, getLineHunkData} from '../helpers/hunk-data-helpers'
import {rowIdFrom} from '../helpers/line-helpers'
import {useGridNavigation} from '../hooks/use-grid-navigation'
import type {DiffLine, LineRange, ClientDiffLine} from '../types'
import {CodeDiffLine} from './DiffLineTableRow'
import ExpandableHunkHeaderDiffLine from './ExpandableHunkHeaderDiffLine'

const UnifiedDiffLines = memo(function UnifiedDiffLines({
  clearSelectedDiffRowRange,
  diffEntryId,
  diffLines,
  fileLineCount,
  fileAnchor,
  filePath,
  tableRef,
  selectedDiffRowRange,
  handleDiffRowClick,
  updateDiffLines,
  updateSelectedDiffRowRange,
  replaceSelectedDiffRowRangeFromGridCells,
  shouldShowCopilot,
}: {
  clearSelectedDiffRowRange: () => void
  diffEntryId: string
  diffLines: DiffLine[]
  fileLineCount: number
  fileAnchor: DiffAnchor
  filePath: string
  tableRef: RefObject<HTMLTableElement>
  selectedDiffRowRange: LineRange | null
  handleDiffRowClick: (
    event: MouseEvent<HTMLTableCellElement>,
    lineNumber: number,
    orientation: 'left' | 'right' | undefined,
    shiftKey: boolean,
  ) => void
  updateDiffLines: (diffAnchor: string, diffLines: DiffLine[]) => void
  updateSelectedDiffRowRange: UpdateSelectedDiffRowRangeFnArgs
  replaceSelectedDiffRowRangeFromGridCells: ReplaceSelectedDiffRowRangeFromGridCellsFnArgs
  shouldShowCopilot: boolean
}) {
  useGridNavigation({
    clearSelectedDiffRowRange,
    containerRef: tableRef,
    fileAnchor,
    isSplitDiff: false,
    leftLines: diffLines as unknown as ClientDiffLine[],
    replaceSelectedDiffRowRangeFromGridCells,
    selectedDiffRowRange,
    updateSelectedDiffRowRange,
  })

  const diffRowSelectedChecker = useMemo(() => {
    if (!selectedDiffRowRange) return undefined
    return DiffRowSelectedChecker({
      selectedDiffRowRange,
      leftLines: diffLines,
    })
  }, [selectedDiffRowRange, diffLines])

  const hunksData = useMemo(() => getDiffHunksData(diffLines.map(diffLine => diffLine)), [diffLines])

  useEffect(() => {
    updateDiffLines(fileAnchor, diffLines)
  }, [diffLines, fileAnchor, updateDiffLines])

  const lines = diffLines.map((line, i) => {
    const currentLine = line
    const nextLine = diffLines[i + 1] as DiffLine | undefined
    const prevLine = diffLines[i - 1] as DiffLine | undefined
    const isHunkRow = line.type === 'HUNK'
    const lineAnchor = lineAnchorFrom(fileAnchor, lineOrientationFrom(currentLine.type), currentLine.blobLineNumber)
    const {currentHunk, nextHunk, previousHunk} = getLineHunkData(currentLine, hunksData)

    const diffLineContextData = {
      diffEntryId,
      diffLine: currentLine,
      currentHunk,
      fileAnchor,
      fileLineCount,
      filePath,
      isLeftSide: currentLine.type === 'DELETION',
      isRowSelected: diffRowSelectedChecker?.isRowSelected(currentLine),
      isSplit: false,
      nextHunk,
      previousHunk,
      rowId: rowIdFrom(fileAnchor, currentLine, currentLine),
    }

    return (
      <Fragment key={i}>
        <tr className="diff-line-row">
          <DiffLineContextProvider {...diffLineContextData}>
            {isHunkRow ? (
              <ExpandableHunkHeaderDiffLine key={i} nextLine={nextLine} prevLine={prevLine} />
            ) : (
              <CodeDiffLine
                shouldShowCopilot={shouldShowCopilot}
                key={i}
                diffLineSummary={<div />}
                filePath={filePath}
                handleDiffRowClick={handleDiffRowClick}
                lineAnchor={lineAnchor}
              />
            )}
          </DiffLineContextProvider>
        </tr>
      </Fragment>
    )
  })

  return <>{lines}</>
})

export default UnifiedDiffLines

try{ UnifiedDiffLines.displayName ||= 'UnifiedDiffLines' } catch {}